import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { filter, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { IDropdownOption } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { ELoadStatus } from '../../../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../../../utilities/http-utilities.service';

import { IFormField } from '../../../../dashboard-content/widget-add-edit-dialog/input-components/form-field-dropdown/form-field-dropdown.store';
import { IDashboard, IWorkspace } from '../../../dashboards/dashboards.interface';
import { IForms } from '../../../form/form.interface';
import { IWidgetRequest } from '../../widget-requests/widget-requests.interface';
import { IFormAutofill, IFormAutofillAddPayload, IFormAutofillEditPayload } from '../form-autofill.interface';
import { IWidgetRequestDropdownOption } from './form-autofill-add-edit-dialog.interface';

export interface IFormAutofillAddEditDialogState {
  widgetRequestsStatus: ELoadStatus;
  widgetRequests: IWidgetRequestDropdownOption[];
  formsStatus: ELoadStatus;
  forms: IDropdownOption[];
  formFieldsStatus: ELoadStatus;
  formFields: IFormField[];
  dashboardStatus: ELoadStatus;
  dashboards: IDropdownOption[];
  workspaceStatus: ELoadStatus;
  workspaces: IDropdownOption[];
  createUpdateStatus: ELoadStatus;
}

@Injectable()
export class FormAutofillAddEditDialogStore extends ComponentStore<IFormAutofillAddEditDialogState> {
  private readonly httpUtilities = inject(HttpUtilitiesService);
  private readonly http = inject(HttpClient);

  public readonly afterCreateUpdateSuccess$ = this.select(({ createUpdateStatus }) => createUpdateStatus).pipe(
    filter((status) => status === ELoadStatus.success),
  );

  public readonly forms: Signal<IDropdownOption[]> = this.selectSignal(({ forms }) => forms);
  public readonly widgetRequests: Signal<IWidgetRequestDropdownOption[]> = this.selectSignal(
    ({ widgetRequests }) => widgetRequests,
  );
  public readonly formFields: Signal<IFormField[]> = this.selectSignal(({ formFields }) => formFields);
  public readonly dashboards = this.selectSignal(({ dashboards }) => dashboards);
  public readonly workspaces = this.selectSignal(({ workspaces }) => workspaces);
  public readonly areFormsLoading: Signal<boolean> = this.selectSignal(
    ({ formsStatus }) => formsStatus === ELoadStatus.loading,
  );
  public readonly areWidgetRequestsLoading: Signal<boolean> = this.selectSignal(
    ({ widgetRequestsStatus }) => widgetRequestsStatus === ELoadStatus.loading,
  );
  public readonly areFormFieldsLoaded: Signal<boolean> = this.selectSignal(
    ({ formFieldsStatus }) => formFieldsStatus === ELoadStatus.success,
  );

  public readonly areWorkspacesLoading = this.selectSignal(
    ({ workspaceStatus }) => workspaceStatus === ELoadStatus.loading,
  );
  public readonly areDashboardsLoading = this.selectSignal(
    ({ dashboardStatus }) => dashboardStatus === ELoadStatus.loading,
  );

  readonly loadForms = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        this.patchState({ formsStatus: ELoadStatus.loading });

        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getForms(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IForms>) =>
              this.patchState({
                forms: response.data.map(({ id, name }) => ({ id, name })),
                formsStatus: ELoadStatus.success,
              }),
            (error) => console.error(error),
          ),
        );
      }),
    ),
  );

  readonly loadWidgetRequests = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params) => {
        this.patchState({ widgetRequestsStatus: ELoadStatus.loading });

        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

        return this.getWidgetRequests(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IWidgetRequest>) =>
              this.patchState({
                widgetRequests: response.data.map(({ id, content, name }) => ({ id, content, name })),
                widgetRequestsStatus: ELoadStatus.success,
              }),
            (error) => console.error(error),
          ),
        );
      }),
    ),
  );

  public readonly loadFormFields = this.effect<number>((trigger$) =>
    trigger$.pipe(
      switchMap((id) => {
        this.patchState({ formFieldsStatus: ELoadStatus.loading });

        return this.getFormFields(id).pipe(
          tapResponse(
            (response: IGetManyResponse<IFormField>) =>
              this.patchState({
                formFields: response.data.filter(
                  ({ type }) => !['button', 'panel', 'columns', 'content', 'table'].includes(type),
                ),
                formFieldsStatus: ELoadStatus.success,
              }),
            (error) => console.error(error),
          ),
        );
      }),
    ),
  );

  readonly loadDashboards = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dashboardStatus: ELoadStatus.loading });

        return this.getDashboards(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                dashboards: response.data.map(({ id, name }) => ({ id, name })),
                dashboardStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadWorkspaces = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ workspaceStatus: ELoadStatus.loading });

        return this.getWorkspaces(httpParams).pipe(
          tapResponse(
            (response) =>
              this.patchState({
                workspaces: response.data.map(({ id, name }) => ({ id, name })),
                workspaceStatus: ELoadStatus.success,
              }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly createOne = this.effect((trigger$: Observable<IFormAutofillAddPayload>) =>
    trigger$.pipe(
      switchMap((payload: IFormAutofillAddPayload) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.createFormAutofill(payload).pipe(
          tapResponse(
            () => {
              this.patchState({ createUpdateStatus: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error creating data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IFormAutofillEditPayload>) =>
    trigger$.pipe(
      switchMap((payload: IFormAutofillEditPayload) => {
        this.patchState({ createUpdateStatus: ELoadStatus.loading });

        return this.updateFormAutofill(payload).pipe(
          tapResponse(
            () => this.patchState({ createUpdateStatus: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error updating data', error),
          ),
        );
      }),
    ),
  );

  constructor() {
    super({
      createUpdateStatus: ELoadStatus.initial,
      dashboards: [],
      dashboardStatus: ELoadStatus.initial,
      formFields: [],
      formFieldsStatus: ELoadStatus.initial,
      forms: [],
      formsStatus: ELoadStatus.initial,
      widgetRequests: [],
      widgetRequestsStatus: ELoadStatus.initial,
      workspaces: [],
      workspaceStatus: ELoadStatus.initial,
    });
  }

  private createFormAutofill(body: IFormAutofillAddPayload): Observable<IBaseResponse<IFormAutofill>> {
    return this.http.post<IBaseResponse<IFormAutofill>>('form-autofills', body);
  }

  private updateFormAutofill(edit: IFormAutofillEditPayload): Observable<IBaseResponse<IFormAutofill>> {
    return this.http.patch<IBaseResponse<IFormAutofill>>(`form-autofills/${edit.id}`, edit.dto);
  }

  private getForms(params: HttpParams): Observable<IGetManyResponse<IForms>> {
    return this.http.get<IGetManyResponse<IForms>>('forms', { params });
  }

  private getWidgetRequests(params: HttpParams): Observable<IGetManyResponse<IWidgetRequest>> {
    return this.http.get<IGetManyResponse<IWidgetRequest>>('widget-requests', { params });
  }

  public getFormFields(id: number): Observable<IGetManyResponse<IFormField>> {
    return this.http.get<IGetManyResponse<IFormField>>(`forms/${id}/fields`);
  }

  private getDashboards(params: HttpParams): Observable<IGetManyResponse<IDashboard>> {
    return this.http.get<IGetManyResponse<IDashboard>>('dashboards', { params });
  }

  private getWorkspaces(params: HttpParams): Observable<IGetManyResponse<IWorkspace>> {
    return this.http.get<IGetManyResponse<IWorkspace>>('workspaces', { params });
  }
}
